/**
 * Cipher — a quote rendered under a monoalphabetic substitution. The player
 * commits whole words; each accepted word unlocks every letter it implies.
 *
 * Ported from iOS `CipherPuzzle.swift`, `CipherGameSession.swift` and
 * `CipherValidator.swift`.
 */

export type CipherDifficulty = 'warmup' | 'standard' | 'tough' | 'brutal';

export interface CipherPuzzle {
  sourceSentence: string;
  sourceAttribution: string | null;
  genre: string;
  difficulty: CipherDifficulty;
  /** plaintext → cipher letter. */
  substitutionMap: Record<string, string>;
  /** cipher → plaintext, the inverse used to reveal. */
  inverseSubstitution: Record<string, string>;
  cipherText: string;
  /** Uppercase set of words that appear in the source sentence. */
  wordsInSource: Set<string>;
}

/** Raw Firestore shape (a `quest.steps[].puzzle` payload). */
export interface CipherLevel {
  sourceSentence?: string;
  sourceAttribution?: string | null;
  genre?: string;
  difficulty?: string;
  substitutionMapString?: string;
  cipherText?: string;
  wordsInSource?: string[];
}

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

const DIFFICULTIES: CipherDifficulty[] = ['warmup', 'standard', 'tough', 'brutal'];

export function normalizePuzzle(raw: CipherLevel | null): CipherPuzzle | null {
  if (!raw) return null;
  const cipherText = (raw.cipherText ?? '').trim();
  const sourceSentence = (raw.sourceSentence ?? '').trim();
  if (!cipherText || !sourceSentence) return null;

  // substitutionMapString is 26 chars: index i is the cipher letter for
  // ALPHABET[i]. Anything else is malformed rather than partially usable.
  const mapString = (raw.substitutionMapString ?? '').toUpperCase();
  if (mapString.length !== 26) return null;

  const substitutionMap: Record<string, string> = {};
  const inverseSubstitution: Record<string, string> = {};
  for (let i = 0; i < 26; i++) {
    const plain = ALPHABET[i];
    const cipher = mapString[i];
    substitutionMap[plain] = cipher;
    inverseSubstitution[cipher] = plain;
  }

  const words = (raw.wordsInSource ?? []).map((w) => String(w).toUpperCase());
  // Fall back to deriving the word set from the source when it wasn't shipped.
  const wordsInSource = new Set(
    words.length
      ? words
      : sourceSentence
          .toUpperCase()
          .split(/\s+/)
          .map(stripPunctuation)
          .filter(Boolean),
  );

  const diffRaw = (raw.difficulty ?? 'standard').toLowerCase();

  return {
    sourceSentence,
    sourceAttribution: raw.sourceAttribution ?? null,
    genre: raw.genre ?? 'wisdom',
    difficulty: (DIFFICULTIES as string[]).includes(diffRaw)
      ? (diffRaw as CipherDifficulty)
      : 'standard',
    substitutionMap,
    inverseSubstitution,
    cipherText,
    wordsInSource,
  };
}

export function stripPunctuation(word: string): string {
  return word.toUpperCase().replace(/[^A-Z]/g, '');
}

/**
 * Canonical shape of a word's repeated-letter structure: "SEES" → [0,1,1,0].
 * Two words can only correspond under a substitution if their patterns match.
 */
export function letterPattern(word: string): string {
  const seen = new Map<string, number>();
  const out: number[] = [];
  let next = 0;
  for (const ch of word) {
    const existing = seen.get(ch);
    if (existing !== undefined) {
      out.push(existing);
    } else {
      seen.set(ch, next);
      out.push(next);
      next++;
    }
  }
  return out.join(',');
}

export type RejectionReason =
  | 'notInDictionary'
  | 'notInSource'
  | 'noLengthMatch'
  | 'patternMismatch'
  | 'substitutionContradiction';

export const REJECTION_MESSAGE: Record<RejectionReason, string> = {
  notInDictionary: 'Not a recognized word.',
  notInSource: "That word isn't in this sentence.",
  noLengthMatch: 'No cipher word matches that length.',
  patternMismatch: "Letter pattern doesn't match any cipher word.",
  substitutionContradiction: "That would contradict letters you've already revealed.",
};

/**
 * Typos shouldn't be punished. Only these reasons represent a real wrong
 * guess — matching `CipherGameSession.mistakeCount`.
 */
export function isMistake(reason: RejectionReason): boolean {
  return (
    reason === 'notInSource' ||
    reason === 'patternMismatch' ||
    reason === 'substitutionContradiction'
  );
}

export type ValidationOutcome =
  | { kind: 'accepted'; newRevealed: Record<string, string> }
  | { kind: 'rejected'; reason: RejectionReason };

/**
 * True iff `proposed` doesn't contradict `existing` and the combined map stays
 * one-to-one — two cipher letters may never map to the same plaintext letter.
 */
function isConsistent(
  proposed: Record<string, string>,
  existing: Record<string, string>,
): boolean {
  for (const [cipher, plain] of Object.entries(proposed)) {
    const known = existing[cipher];
    if (known !== undefined && known !== plain) return false;
  }

  const combined = { ...existing };
  for (const [cipher, plain] of Object.entries(proposed)) {
    if (combined[cipher] === undefined) {
      for (const [otherCipher, otherPlain] of Object.entries(combined)) {
        if (otherCipher !== cipher && otherPlain === plain) return false;
      }
    }
    combined[cipher] = plain;
  }
  return true;
}

/**
 * Five sequential checks, in the iOS order: dictionary → source membership →
 * length match → letter-pattern match → substitution consistency.
 */
export function validate(
  rawGuess: string,
  puzzle: CipherPuzzle,
  currentlyRevealed: Record<string, string>,
  isDictionaryWord: (word: string) => boolean,
): ValidationOutcome {
  const guess = stripPunctuation(rawGuess);
  if (!guess) return { kind: 'rejected', reason: 'notInDictionary' };

  // Short words bypass the dictionary: the bundled wordlist ships no 1- or
  // 2-letter entries, so A/I/OF/TO/IS would always be rejected. They stay
  // constrained by the wordsInSource check below.
  if (guess.length > 2 && !isDictionaryWord(guess)) {
    return { kind: 'rejected', reason: 'notInDictionary' };
  }

  if (!puzzle.wordsInSource.has(guess)) {
    return { kind: 'rejected', reason: 'notInSource' };
  }

  const cipherWords = puzzle.cipherText
    .split(/\s+/)
    .map(stripPunctuation)
    .filter(Boolean);

  const lengthMatches = cipherWords.filter((w) => w.length === guess.length);
  if (!lengthMatches.length) return { kind: 'rejected', reason: 'noLengthMatch' };

  const guessPattern = letterPattern(guess);
  const patternMatches = lengthMatches.filter((w) => letterPattern(w) === guessPattern);
  if (!patternMatches.length) return { kind: 'rejected', reason: 'patternMismatch' };

  // Accept as soon as one candidate cipher word is fully consistent, unioning
  // its substitutions into the revealed map.
  for (const cipherWord of patternMatches) {
    const proposed: Record<string, string> = {};
    for (let i = 0; i < cipherWord.length; i++) proposed[cipherWord[i]] = guess[i];

    if (isConsistent(proposed, currentlyRevealed)) {
      const newRevealed: Record<string, string> = {};
      for (const [c, p] of Object.entries(proposed)) {
        if (currentlyRevealed[c] === undefined) newRevealed[c] = p;
      }
      return { kind: 'accepted', newRevealed };
    }
  }

  return { kind: 'rejected', reason: 'substitutionContradiction' };
}

/** Solved once every cipher letter appearing in the text has been mapped. */
export function isSolved(
  puzzle: CipherPuzzle,
  revealed: Record<string, string>,
): boolean {
  for (const ch of puzzle.cipherText) {
    if (/[A-Za-z]/.test(ch) && revealed[ch.toUpperCase()] === undefined) return false;
  }
  return true;
}

/**
 * The highest-frequency unrevealed cipher letter — each hint therefore unlocks
 * the most positions. Ties break alphabetically for determinism.
 */
export function bestUnrevealedLetter(
  puzzle: CipherPuzzle,
  revealed: Record<string, string>,
): string | null {
  const counts = new Map<string, number>();
  for (const ch of puzzle.cipherText) {
    if (!/[A-Za-z]/.test(ch)) continue;
    const up = ch.toUpperCase();
    if (revealed[up] !== undefined) continue;
    counts.set(up, (counts.get(up) ?? 0) + 1);
  }
  let best: string | null = null;
  let bestCount = -1;
  for (const [letter, count] of counts) {
    if (count > bestCount || (count === bestCount && best !== null && letter < best)) {
      best = letter;
      bestCount = count;
    }
  }
  return best;
}

/** Cipher-letter frequencies, descending — powers the frequency panel. */
export function letterFrequencies(
  puzzle: CipherPuzzle,
): Array<{ letter: string; count: number }> {
  const counts = new Map<string, number>();
  for (const ch of puzzle.cipherText) {
    if (!/[A-Za-z]/.test(ch)) continue;
    const up = ch.toUpperCase();
    counts.set(up, (counts.get(up) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([letter, count]) => ({ letter, count }))
    .sort((a, b) => b.count - a.count || a.letter.localeCompare(b.letter));
}
